import {
  isAgeVerificationMandatory,
  isAgeVerificationOptional,
  isAgeVerificationVerified,
  useTranslation,
  useUserInfo,
} from "@next-platform/client-common-core";
import { ButtonSize, ButtonVariant, Modal, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { JSX, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ROUTES } from "@/constants";
import { useAgeVerificationFlags } from "@/hooks";
import { getAgeVerificationModalShownFlag, setAgeVerificationModalShownFlag, UTCtimestampToString } from "@/utils";

import Trans from "../Trans/Trans";

type AgeVerificationWarningModalProps = {
  isOpen?: boolean;
  onClose: () => void;
  showNSFW?: boolean;
  // use to force open the modal everytime regardless of the localstorage or showNSFW value
  forceIsRequired?: boolean;
  version?: "chat";
};

export const AgeVerificationWarningModal = ({
  isOpen = false,
  onClose,
  showNSFW,
  forceIsRequired,
  version,
}: AgeVerificationWarningModalProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAgeVerificationEnabled } = useAgeVerificationFlags();
  const { userProfile } = useUserInfo();
  const [isRequired, setIsRequired] = useState(forceIsRequired || false);
  const [isForceOpen, setIsForceOpen] = useState(false);
  const age_verification_modal_shown = getAgeVerificationModalShownFlag();
  const userAgeVerificationStatus = userProfile?.age_verification?.status;

  useEffect(() => {
    if (userProfile && !forceIsRequired && isAgeVerificationEnabled) {
      setIsRequired(isAgeVerificationMandatory(userProfile?.age_verification?.status));
    } else if (!isAgeVerificationEnabled) {
      setIsRequired(false);
    }
  }, [userProfile?.age_verification?.status, forceIsRequired, isAgeVerificationEnabled]);

  useEffect(() => {
    if (isOpen || isForceOpen) {
      // just making sure the modal is not shown again if the user closes it and opens the page again OR refresh it
      setAgeVerificationModalShownFlag("true");
    }
  }, [isOpen, isForceOpen]);

  useEffect(() => {
    // making sure the modal appears if age verification is mandatory
    // or if it's optional (but not verified) and the user never saw it before
    const isMandatory = isAgeVerificationMandatory(userAgeVerificationStatus);
    const isOptional = isAgeVerificationOptional(userAgeVerificationStatus);
    const isVerified = isAgeVerificationVerified(userAgeVerificationStatus);

    if (!isOpen && showNSFW && isAgeVerificationEnabled && !isVerified && (isMandatory || (isOptional && !age_verification_modal_shown))) {
      setIsForceOpen(true);
    }
  }, [isOpen, isAgeVerificationEnabled, showNSFW, age_verification_modal_shown, userAgeVerificationStatus]);

  if (!isAgeVerificationEnabled) {
    return <></>;
  }

  const sharedActionStyles = {
    className: "flex-1 min-w-0 !px-3 sm:!px-4",
    classNames: { text: "!whitespace-nowrap text-center !leading-tight !text-[13px]" },
  };

  const modalActions = [
    {
      label: t("common:modal.ageVerification.cta.verifyNow"),
      variant: ButtonVariant.CONTAINED,
      size: ButtonSize.LARGE,
      ...sharedActionStyles,
      onPress: () => {
        navigate(ROUTES.ageVerification);
      },
    },
  ];

  if (!forceIsRequired) {
    modalActions.unshift({
      label:
        version === "chat"
          ? t("common:cancel")
          : isRequired
            ? t("common:modal.ageVerification.cta.continueWithoutNsfw")
            : t("common:modal.ageVerification.cta.verifyLater"),
      variant: ButtonVariant.OUTLINED,
      size: ButtonSize.LARGE,
      ...sharedActionStyles,
      onPress: () => {
        onClose();
        setIsForceOpen(false);
      },
    });
  }

  return (
    <Modal
      title={`🔞 ${t("common:modal.ageVerification.title")}`}
      open={isOpen || isForceOpen}
      onOpenChange={() => {
        onClose();
        if (isForceOpen) {
          setIsForceOpen(false);
        }
      }}
      actions={modalActions}
    >
      <Text variant={TextVariants.LABEL_LARGE_REGULAR}>
        {isRequired ? (
          <Trans i18nKey="common:modal.ageVerification.description.required" />
        ) : (
          <Trans i18nKey="common:modal.ageVerification.description.optional" />
        )}
      </Text>
      {isRequired ? (
        <Text variant={TextVariants.LABEL_LARGE_REGULAR}>
          <Trans i18nKey="common:modal.ageVerification.description.nsfwDisabled" />
        </Text>
      ) : null}
      {!isRequired && userProfile?.age_verification?.mandatoryAfterDate ? (
        <Text variant={TextVariants.LABEL_LARGE_MEDIUM}>
          <Trans i18nKey="common:modal.ageVerification.verificationRequiredBy" />:{" "}
          {UTCtimestampToString(userProfile?.age_verification?.mandatoryAfterDate)}
        </Text>
      ) : null}
    </Modal>
  );
};
